"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { socialAPI, handleApiError, getUserClinicAppointments, petReminderAPI } from "../../../services/api";
import { getUserPlaceholderImage } from "../../../utils/imageUtils";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import { USER_TYPES } from "../../../constants/userTypes";
import "./RightSidebar.css";

const RightSidebar = ({
  suggestedConnections: propSuggestedConnections = null,
  feedSettled = false,
}) => {
  const { getUserType } = useAuth();
  const [suggestedConnections, setSuggestedConnections] = useState(
    propSuggestedConnections
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointmentsError, setAppointmentsError] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [remindersError, setRemindersError] = useState(null);
  const hasFetchedRef = useRef(false);
  const appointmentsFetchedRef = useRef(false);
  const remindersFetchedRef = useRef(false);
  const [dismissingIds, setDismissingIds] = useState(new Set());
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Get user type color
  const currentUserType = getUserType();
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8', // Purple
      'VETERINARIAN': '#00D4B3', // Cyan
      'SERVICE_PROVIDER': '#D65745', // Orange
      'CLINIC_MODERATOR': '#2A8AD2', // Blue
      'FIRM': '#1B3A6B',           // Navy Blue
    };
    return userTypeConfig[userType] || '#8548A8'; // Default to purple
  };
  const userTypeColor = getUserTypeColor(currentUserType);

  // Mock trending topics
  const trendingTopics = useMemo(() => {
    const topics = t("sideBar.trendingTags", { returnObjects: true });
    return Array.isArray(topics) ? topics : [];
  }, [t]);

  // Update suggested connections when prop changes
  useEffect(() => {
    if (propSuggestedConnections && propSuggestedConnections.length > 0) {
      setSuggestedConnections(propSuggestedConnections);
      setLoading(false);
      setError(null);
    } else if (
      propSuggestedConnections &&
      propSuggestedConnections.length === 0
    ) {
      setSuggestedConnections([]);
      setLoading(false);
      setError(null);
    }
  }, [propSuggestedConnections]);

  // Fetch suggested connections from API only if the consolidated feed failed to
  // deliver them (props stay null on feed error). An array from the feed — even
  // empty — is final: the standalone endpoint runs the same query and would
  // return the same result.
  useEffect(() => {
    // null = feed hasn't delivered (or failed); non-null = decided by the feed
    if (propSuggestedConnections) {
      return;
    }

    // Wait for the consolidated feed to settle before falling back
    if (!feedSettled) {
      return;
    }

    // Prevent multiple API calls
    if (hasFetchedRef.current) {
      return;
    }

    hasFetchedRef.current = true;

    const abortController = new AbortController();

    const fetchSuggestedConnections = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await socialAPI.getSuggestedConnections();

        // Check if the request was aborted
        if (abortController.signal.aborted) {
          return;
        }

        if (response.data && response.data.data) {
          // Filter out ADMIN and CLINIC_MODERATOR users from suggested connections
          // CLINIC_MODERATOR users should only appear in clinic team management
          const filteredConnections = response.data.data.filter(
            connection =>
              connection.user_type !== 'ADMIN' &&
              connection.user_type !== 'admin' &&
              connection.user_type !== 'CLINIC_MODERATOR' &&
              connection.user_type !== 'clinic_moderator'
          );

          // Transform the API response to match our component structure
          const transformedConnections = filteredConnections.map(
            (connection) => ({
              id: connection.id || connection.user_id,
              user_id: connection.user_id || connection.id, // Add user_id for navigation
              name:
                `${connection.first_name || ""} ${connection.last_name || ""
                  }`.trim() ||
                connection.username ||
                t("sideBar.unknownUser"),
              avatar: (() => {
                // Check if profile_image_url exists and is not a placeholder
                if (connection.profile_image_url && !connection.profile_image_url.includes('placeholder')) {
                  return connection.profile_image_url;
                }
                // Check if avatar exists and is not a placeholder
                if (connection.avatar && !connection.avatar.includes('placeholder')) {
                  return connection.avatar;
                }
                // Default fallback
                return getUserPlaceholderImage(connection);
              })(),
              mutualConnections: connection.mutual_connections_count || 0,
              company: connection.user_type || t("socialSidebar.fallbackRole"),
              username: connection.username || `@${connection.id || connection.user_id}`,
            })
          );

          setSuggestedConnections(transformedConnections);
        } else {
          setSuggestedConnections([]);
        }
      } catch (err) {
        // Don't set error if request was aborted
        if (!abortController.signal.aborted) {
          console.error("Error fetching suggested connections:", err);
          setError(handleApiError(err));
          // Fallback to empty array on error
          setSuggestedConnections([]);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchSuggestedConnections();

    // Cleanup function to abort the request if component unmounts or effect re-runs
    return () => {
      abortController.abort();
    };
  }, [propSuggestedConnections, feedSettled]);

  // Fetch user's upcoming appointments
  useEffect(() => {
    // Prevent multiple API calls
    if (appointmentsFetchedRef.current) {
      return;
    }

    // FIRM users have no care access — skip fetch entirely
    if (currentUserType === 'FIRM') {
      return;
    }

    appointmentsFetchedRef.current = true;

    const abortController = new AbortController();

    const fetchUpcomingAppointments = async () => {
      try {
        setAppointmentsLoading(true);
        setAppointmentsError(null);
        const response = await getUserClinicAppointments({
          status: "upcoming",
          per_page: 10,
          sort_by: "appointment_at",
          sort_order: "asc",
        });

        if (abortController.signal.aborted) {
          return;
        }

        const appointmentsData =
          response?.data?.data?.appointments ||
          response?.data?.appointments ||
          [];

        if (appointmentsData.length > 0) {
          const now = new Date();
          // Get today's date in YYYY-MM-DD format for accurate date-only comparison
          const todayStr = now.toISOString().split('T')[0];
          const todayDate = new Date(todayStr + 'T00:00:00');

          const locale = i18n.language || (document?.documentElement?.lang ?? "en");
          const userType = getUserType();
          let filteredAppointments = appointmentsData.filter(appointment => {
            if (!appointment?.appointment_at) {
              return false;
            }

            // Check terminal statuses first - these should always be filtered out
            const appointmentStatus = appointment.status?.toLowerCase();
            const isTerminalStatus = appointmentStatus === 'completed' ||
              appointmentStatus === 'cancelled' ||
              appointmentStatus === 'canceled' ||
              appointmentStatus === 'no_show' ||
              appointmentStatus === 'no show';

            if (isTerminalStatus) {
              return false; // Always filter out terminal statuses
            }

            // Parse appointment date
            const normalizedDateString =
              typeof appointment.appointment_at === "string"
                ? appointment.appointment_at.replace(" ", "T")
                : appointment.appointment_at;
            const appointmentDate = new Date(normalizedDateString);
            if (Number.isNaN(appointmentDate.getTime())) {
              return false;
            }

            // Get appointment date in YYYY-MM-DD format for accurate date-only comparison
            const appointmentDateStr = appointmentDate.toISOString().split('T')[0];
            const appointmentDateOnly = new Date(appointmentDateStr + 'T00:00:00');

            // Only show upcoming (future or today) appointments
            return appointmentDateOnly >= todayDate;
          });
          if (userType === 'VETERINARIAN') {
            filteredAppointments = filteredAppointments.filter(appointment => {
              return appointment.status === 'pending';
            });
          }
          // Only use filtered appointments - don't fallback to all appointments
          // If no upcoming appointments, show empty list
          const sortedAppointments = filteredAppointments
            .map((appointment) => ({
              appointment,
              date: (() => {
                const normalizedDateString =
                  typeof appointment.appointment_at === "string"
                    ? appointment.appointment_at.replace(" ", "T")
                    : appointment.appointment_at;
                return new Date(normalizedDateString);
              })(),
            }))
            .filter(({ date }) => !Number.isNaN(date.getTime()))
            .sort((a, b) => a.date - b.date)
            .map(({ appointment }) => appointment);

          const closestTwo = sortedAppointments.slice(0, 2);
          const transformedAppointments = closestTwo.map(appointment => {
            const normalizedDateString =
              typeof appointment.appointment_at === "string"
                ? appointment.appointment_at.replace(" ", "T")
                : appointment.appointment_at;
            const appointmentDate = new Date(normalizedDateString);
            const today = new Date();
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            const timeOptions = { hour: '2-digit', minute: '2-digit' };
            const localizedTime = appointmentDate.toLocaleTimeString(locale, timeOptions);
            let timeDisplay;
            if (appointmentDate.toDateString() === today.toDateString()) {
              timeDisplay = t("sideBar.todayAt", { time: localizedTime });
            } else if (appointmentDate.toDateString() === tomorrow.toDateString()) {
              timeDisplay = t("sideBar.tomorrowAt", { time: localizedTime });
            } else {
              const localizedDate = appointmentDate.toLocaleDateString(locale, {
                month: 'short',
                day: 'numeric',
              });
              timeDisplay = t("sideBar.dateAt", { date: localizedDate, time: localizedTime });
            }
            const petName = appointment.pet
              ? `${typeof appointment.pet.name === 'object' ? appointment.pet.name?.name : appointment.pet.name || ''} ${typeof appointment.pet.species === 'object' ? appointment.pet.species?.name : appointment.pet.species || ''}`.trim() || t("sideBar.unknownPet")
              : t("sideBar.unknownPet");
            const clinicName = appointment.clinic?.name_ar || appointment.clinic?.name_en || appointment.clinic?.name || t("sideBar.unknownClinic");
            return {
              id: appointment.id,
              service:
                appointment.appointment_type?.name ||
                appointment.appointment_type?.title ||
                appointment.reason ||
                t("sideBar.unknownAppointment"),
              time: timeDisplay,
              petName,
              clinicName,
            };
          });
          setUpcomingAppointments(transformedAppointments);
        } else {
          setUpcomingAppointments([]);
        }
      } catch (err) {
        // Don't set error if request was aborted
        if (!abortController.signal.aborted) {
          console.error("Error fetching upcoming appointments:", err);
          setAppointmentsError(handleApiError(err));
          // Fallback to empty array on error
          setUpcomingAppointments([]);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setAppointmentsLoading(false);
        }
      }
    };

    fetchUpcomingAppointments();

    // Cleanup function to abort the request if component unmounts or effect re-runs
    return () => {
      abortController.abort();
    };
  }, []);

  // Fetch pet vaccination reminders
  useEffect(() => {
    if (remindersFetchedRef.current) {
      return;
    }

    if (currentUserType === 'VETERINARIAN' || currentUserType === 'FIRM') {
      return;
    }

    remindersFetchedRef.current = true;

    const abortController = new AbortController();

    const fetchReminders = async () => {
      try {
        setRemindersLoading(true);
        setRemindersError(null);

        const response = await petReminderAPI.getReminders();

        if (abortController.signal.aborted) {
          return;
        }

        const remindersData = response?.data?.data || [];
        setReminders(remindersData);
      } catch (err) {
        if (!abortController.signal.aborted) {
          console.error("Error fetching pet reminders:", err);
          setRemindersError(handleApiError(err));
          setReminders([]);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setRemindersLoading(false);
        }
      }
    };

    fetchReminders();

    return () => {
      abortController.abort();
    };
  }, []);

  const handleDismissReminder = async (vaccinationId) => {
    try {
      setDismissingIds((prev) => new Set(prev).add(vaccinationId));
      await petReminderAPI.dismissReminder(vaccinationId);
      setReminders((prev) => prev.filter((r) => r.id !== vaccinationId));
    } catch (err) {
      console.error("Error dismissing reminder:", err);
    } finally {
      setDismissingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(vaccinationId);
        return newSet;
      });
    }
  };

  const getReminderDueText = (reminder) => {
    if (reminder.is_overdue) {
      const days = Math.abs(reminder.days_until);
      return t("sideBar.overdueByDays", { count: days });
    }
    if (reminder.days_until === 0) {
      return t("sideBar.dueToday");
    }
    if (reminder.days_until === 1) {
      return t("sideBar.dueTomorrow");
    }
    return t("sideBar.dueInDays", { count: reminder.days_until });
  };

  const handleViewAll = (section) => {
    if (section === "appointments") {
      const userType = getUserType();

      // Route based on user type
      if (userType === USER_TYPES.VETERINARIAN || userType === USER_TYPES.CLINIC_MODERATOR) {
        navigate("/medical/clinic/redirect-to-clinic");
      } else {
        // Default behavior for Pet Guardians and other user types
        navigate("/medical/dashboard/petguardian?tab=appointments");
      }
    }
  };

  const [connectingUsers, setConnectingUsers] = useState(new Set());
  const [sentRequests, setSentRequests] = useState(new Set());

  const handleConnect = async (userId) => {
    try {
      setConnectingUsers((prev) => new Set(prev).add(userId));

      await socialAPI.sendConnectionRequest(userId);

      // Add to sent requests instead of removing from list
      setSentRequests((prev) => new Set(prev).add(userId));
    } catch (err) {
      console.error("Error sending connection request:", err);
      // You could show a toast notification here
    } finally {
      setConnectingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  return (
    <>
      {/* Desktop and Tablet Right Sidebar */}
      <div className="right-sidebar-desktop">
        {/* Upcoming Appointments - Hidden for VET and FIRM users */}
        {currentUserType !== 'VETERINARIAN' && currentUserType !== 'FIRM' && (
          <div className="right-sidebar-card">
            <div className="right-sidebar-header">
              <p className="right-sidebar-title small">
                {t("sideBar.upcomingAppointments")}
              </p>
              <button
                onClick={() => handleViewAll("appointments")}
                className="view-all-btn"
              >
                {t("sideBar.viewAll")}
              </button>
            </div>

            {appointmentsLoading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="skeleton-bar" style={{ height: '0.75rem', width: '100%' }}></div>
                    <div className="skeleton-bar" style={{ height: '0.5rem', width: '50%' }}></div>
                    <div className="skeleton-bar" style={{ height: '0.5rem', width: '75%' }}></div>
                  </div>
                ))}
              </div>
            ) : appointmentsError ? (
              <div className="error-message">
                <p className="error-text">{appointmentsError}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="view-all-btn"
                >
                  {t("sideBar.tryagain")}
                </button>
              </div>
            ) : upcomingAppointments.length === 0 ? (
              <div className="empty-state">
                <p className="empty-text">{t("sideBar.noUpcomingAppointments") || "No upcoming appointments"}</p>
              </div>
            ) : (
              <div className="appointments-list appointments-scrollable">
                {upcomingAppointments.map((appointment, idx) => (
                  <div
                    key={appointment.id || `appointment-${idx}`}
                    className="appointment-item"
                  >
                    <p className="appointment-service">
                      {appointment.service}
                    </p>
                    <p className="appointment-time">
                      {appointment.time}
                    </p>
                    <p className="appointment-pet">
                      {appointment.petName}
                    </p>
                    {appointment.clinicName && (
                      <p className="appointment-clinic">
                        {appointment.clinicName}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pet Reminders — only show if there are actual reminders */}
        {currentUserType !== 'VETERINARIAN' && currentUserType !== 'FIRM' && reminders.length > 0 && (
          <div className="right-sidebar-card">
            <div className="right-sidebar-header">
              <p className="right-sidebar-title small">
                {t("sideBar.petReminders")}
              </p>
            </div>

            <div className="reminders-list reminders-scrollable">
              {reminders.map((reminder, idx) => (
                <div
                  key={reminder.id || `reminder-${idx}`}
                  className="reminder-item"
                >
                  <div className="reminder-main">
                    <img
                      src={reminder.pet_image || "/images/no-image-pet.png"}
                      alt={reminder.pet_name}
                      className="reminder-pet-avatar"
                      onError={(e) => {
                        e.target.src = "/images/no-image-pet.png";
                      }}
                    />
                    <div className="reminder-info">
                      <p className="reminder-pet-name">{reminder.pet_name}</p>
                      <p className="reminder-vaccine">{reminder.vaccine_name}</p>
                      <p className={`reminder-due ${reminder.is_overdue ? 'reminder-due--urgent' : reminder.days_until <= 1 ? 'reminder-due--soon' : ''}`}>
                        {getReminderDueText(reminder)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDismissReminder(reminder.id)}
                    disabled={dismissingIds.has(reminder.id)}
                    className="reminder-dismiss-btn"
                    title={t("sideBar.dontRemindMe")}
                  >
                    {dismissingIds.has(reminder.id) ? "..." : t("sideBar.dontRemindMe")}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Suggested Connections */}
        <div className="right-sidebar-card">
          <div className="right-sidebar-header">
            <p className="right-sidebar-title small">
              {t("sideBar.suggestedConnections")}
            </p>
          </div>

          {loading ? (
            <div key="loading" className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="flex items-center justify-between animate-pulse"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="skeleton-bar" style={{ width: '2rem', height: '2rem', borderRadius: '50%' }}></div>
                    <div className="min-w-0 flex-1">
                      <div className="skeleton-bar skeleton-text"></div>
                      <div className="skeleton-bar skeleton-subtext"></div>
                    </div>
                  </div>
                  <div className="skeleton-bar" style={{ width: '3rem', height: '1rem' }}></div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div key="error" className="error-message">
              <p className="error-text">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="view-all-btn"
              >
                {t("sideBar.tryagain")}
              </button>
            </div>
          ) : suggestedConnections.length === 0 ? (
            <div key="empty" className="empty-state">
              <p className="empty-text">{t("sideBar.nosuggest")}</p>
            </div>
          ) : (
            <div key="connections">
              <div
                className="suggested-connections-list suggested-connections-scrollable"
              >
                {suggestedConnections.slice(0, 3).map((connection, index) => (
                  <div
                    key={connection.user_id || `connection-${index}`}
                    className="connection-item"
                  >
                    <div className="connection-info">
                      <img
                        src={connection.avatar || "/placeholder.svg"}
                        alt={connection.name}
                        className="connection-avatar"
                        onError={(e) => {
                          e.target.src = getUserPlaceholderImage(connection);
                        }}
                      />
                      <div className="connection-details">
                        <button
                          onClick={() => {
                            const userId = connection.user_id || connection.id;
                            navigate(`/social/profile/${userId}`);
                          }}
                          className="connection-name"
                        >
                          {(connection.name || "").length > 13 ? (connection.name || "").slice(0, 13) + '...' : (connection.name || "")}
                        </button>
                        {connection.mutualConnections > 0 && (
                          <p className="connection-mutual">
                            {connection.mutualConnections} {t("sideBar.mutual")}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleConnect(connection.user_id)}
                      disabled={connectingUsers.has(connection.user_id) || sentRequests.has(connection.user_id)}
                      title={sentRequests.has(connection.user_id) ? t("sideBar.sent") || "Sent" : connectingUsers.has(connection.user_id) ? t("sideBar.sending") : t("sideBar.requestToBuddy")}
                      aria-label={sentRequests.has(connection.user_id) ? t("sideBar.sent") || "Sent" : connectingUsers.has(connection.user_id) ? t("sideBar.sending") : t("sideBar.requestToBuddy")}
                      className="connect-btn"
                      style={{
                        backgroundColor: sentRequests.has(connection.user_id)
                          ? '#E5E7EB'
                          : connectingUsers.has(connection.user_id)
                            ? '#F3F4F6'
                            : userTypeColor,
                        color: sentRequests.has(connection.user_id)
                          ? '#6B7280'
                          : connectingUsers.has(connection.user_id)
                            ? '#9CA3AF'
                            : '#FFFFFF',
                        width: '32px', /* Fixed width/height for icon to match design if needed, or keeping it auto */
                        minWidth: 'auto',
                        padding: '0', /* Assuming icon only based on svg */
                        width: '32px'
                      }}
                      onMouseEnter={(e) => {
                        if (!connectingUsers.has(connection.user_id) && !sentRequests.has(connection.user_id)) {
                          e.currentTarget.style.opacity = '0.9';
                          e.currentTarget.style.transform = 'scale(1.02)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!connectingUsers.has(connection.user_id) && !sentRequests.has(connection.user_id)) {
                          e.currentTarget.style.opacity = '1';
                          e.currentTarget.style.transform = 'scale(1)';
                        }
                      }}
                    >
                      {sentRequests.has(connection.user_id) ? (
                        /* Icon for Sent */
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      ) : connectingUsers.has(connection.user_id) ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="16"></line>
                          <line x1="8" y1="12" x2="16" y2="12"></line>
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="5" x2="12" y2="19"></line>
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Right Sidebar - Bottom Sheet Style */}
      <div className="right-sidebar-mobile">
        {/* Mobile handle */}
        <div className="right-sidebar-mobile-handle"></div>

        {/* Mobile content - Compact versions of desktop sections */}
        <div className="right-sidebar-mobile-content">
          {/* Upcoming Appointments - Mobile - Hidden for VET and FIRM users */}
          {currentUserType !== 'VETERINARIAN' && currentUserType !== 'FIRM' && (
            <div className="right-sidebar-card">
              <div className="right-sidebar-header">
                <p className="right-sidebar-title">
                  {t("sideBar.appointments")}
                </p>
                <button
                  onClick={() => handleViewAll("appointments")}
                  className="view-all-btn"
                >
                  {t("sideBar.viewAll")}
                </button>
              </div>

              {appointmentsLoading ? (
                <div className="space-y-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="skeleton-bar" style={{ height: '0.75rem', width: '100%' }}></div>
                      <div className="skeleton-bar" style={{ height: '0.5rem', width: '50%' }}></div>
                      <div className="skeleton-bar" style={{ height: '0.5rem', width: '75%' }}></div>
                    </div>
                  ))}
                </div>
              ) : appointmentsError ? (
                <div className="error-message">
                  <p className="error-text">{appointmentsError}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="view-all-btn"
                  >
                    {t("sideBar.tryagain")}
                  </button>
                </div>
              ) : upcomingAppointments.length === 0 ? (
                <div className="empty-state">
                  <p className="empty-text">{t("sideBar.noUpcomingAppointments") || "No upcoming appointments"}</p>
                </div>
              ) : (
                <div
                  className="appointments-list appointments-scrollable-mobile"
                >
                  {upcomingAppointments.map((appointment) => (
                    <div
                      key={appointment.id}
                      className="appointment-item"
                      style={{ cursor: 'pointer' }}
                    >
                      <p className="appointment-service">
                        {appointment.service}
                      </p>
                      <p className="appointment-time">
                        {appointment.time}
                      </p>
                      <p className="appointment-pet">
                        {appointment.petName}
                      </p>
                      {appointment.clinicName && (
                        <p className="appointment-clinic">
                          {appointment.clinicName}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Trending Topics - Mobile - Commented out as requested
          <div className="right-sidebar-card no-bg">
            <p className="right-sidebar-title mb-3">
              {t("sideBar.trendingTopics")}
            </p>
            <div
              className="trending-topics-list trending-topics-scrollable-mobile"
            >
              {trendingTopics.map((topic, index) => (
                <span
                  key={index}
                  className="trending-topic-tag"
                >
                  {topic}
                </span>
              ))}
            </div>
          </div>
          */}

          {/* Suggested Connections - Mobile */}
          <div className="right-sidebar-card">
            <div className="right-sidebar-header">
              <p className="right-sidebar-title">
                {t("sideBar.suggestedConnections")}
              </p>
            </div>

            {loading ? (
              <div key="mobile-loading" className="space-y-3">
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between animate-pulse"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="skeleton-bar" style={{ width: '2rem', height: '2rem', borderRadius: '50%' }}></div>
                      <div className="min-w-0 flex-1">
                        <div className="skeleton-bar skeleton-text"></div>
                        <div className="skeleton-bar skeleton-subtext"></div>
                      </div>
                    </div>
                    <div className="skeleton-bar" style={{ width: '3rem', height: '1rem' }}></div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div key="mobile-error" className="error-message">
                <p className="error-text">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="view-all-btn"
                >
                  {t("sideBar.tryagain")}
                </button>
              </div>
            ) : suggestedConnections.length === 0 ? (
              <div key="mobile-empty" className="empty-state">
                <p className="empty-text">
                  {t("sideBar.nosuggest")}
                </p>
              </div>
            ) : (
              <div key="mobile-connections">
                <div
                  className="suggested-connections-list suggested-connections-scrollable-mobile"
                >
                  {suggestedConnections.slice(0, 3).map((connection, index) => (
                    <div
                      key={connection.id || `mobile-connection-${index}`}
                      className="connection-item"
                    >
                      <div className="connection-info">
                        <img
                          src={connection.avatar || "/placeholder.svg"}
                          alt={connection.name}
                          className="connection-avatar"
                          onError={(e) => {
                            e.target.src = getUserPlaceholderImage(connection);
                          }}
                        />
                        <div className="connection-details">
                          <p className="connection-name">
                            {connection.name}
                          </p>
                          <p className="connection-mutual" style={{ fontSize: '12px' }}>
                            {connection.company}
                          </p>
                          {connection.mutualConnections > 0 && (
                            <p className="connection-mutual">
                              {connection.mutualConnections} {t("sideBar.mutual")}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleConnect(connection.id)}
                        disabled={connectingUsers.has(connection.id) || sentRequests.has(connection.id)}
                        title={sentRequests.has(connection.id) ? t("sideBar.sent") || "Sent" : connectingUsers.has(connection.id) ? t("sideBar.sending") : t("sideBar.requestToBuddy")}
                        aria-label={sentRequests.has(connection.id) ? t("sideBar.sent") || "Sent" : connectingUsers.has(connection.id) ? t("sideBar.sending") : t("sideBar.requestToBuddy")}
                        className="connect-btn"
                        style={{
                          backgroundColor: sentRequests.has(connection.id)
                            ? '#E5E7EB'
                            : connectingUsers.has(connection.id)
                              ? '#F3F4F6'
                              : '#2a8ad2', /* hardcoded blue for mobile */
                          color: sentRequests.has(connection.id)
                            ? '#6B7280'
                            : connectingUsers.has(connection.id)
                              ? '#9CA3AF'
                              : '#FFFFFF',
                          minWidth: '60px'
                        }}
                      >
                        {sentRequests.has(connection.id) ? (
                          <span>{t("sideBar.sent") || "Sent"}</span>
                        ) : connectingUsers.has(connection.id) ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="16"></line>
                            <line x1="8" y1="12" x2="16" y2="12"></line>
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
};

export default RightSidebar;
